import React, { useState, useMemo, useCallback, useEffect, useRef, } from 'react';
import { X, Check, Search, Loader2 } from 'lucide-react';
import { debounce } from 'lodash';
import { BadgeAlt, Button, Calendar, Checkbox, Input, Switch, } from '@cargomatic/ui';
import { FilterDatePicker } from './FilterDatePicker';
/**
 * Auto-renders the right filter editor based on config.kind.
 * Used inside FilterChip's popover to edit a filter's value.
 *
 * Renders Tasks-demo-style UI: selected badges at top + search + checkbox list.
 */
export function FilterValueEditor({ config, value, onChange, }) {
    switch (config.kind) {
        case 'select-multi':
            return (React.createElement(MultiSelectEditor, { options: config.options, value: (value || []), onChange: onChange }));
        case 'select-single':
            return (React.createElement(SingleSelectEditor, { options: config.options, value: (value || null), onChange: onChange }));
        case 'text':
            return (React.createElement(TextEditor, { value: (value || ''), onChange: onChange, placeholder: config.placeholder }));
        case 'date-range':
            return (React.createElement(Calendar, { mode: "range", numberOfMonths: 2, selected: value, onSelect: (range) => onChange(range || null) }));
        case 'date':
            return (React.createElement(FilterDatePicker, { value: value || null, onChange: (date) => onChange(date || null), placeholder: config.placeholder }));
        case 'toggle':
            return (React.createElement(ToggleEditor, { value: !!value, onChange: onChange, label: config.label }));
        case 'autocomplete-single':
            if (!config.loadOptions) {
                return (React.createElement("div", { className: "text-sm text-muted-foreground py-4 text-center" }, "No loadOptions configured"));
            }
            return (React.createElement(AutocompleteSingleEditor, { loadOptions: config.loadOptions, value: (value || null), onChange: onChange, placeholder: config.placeholder }));
        case 'autocomplete-multi':
            if (!config.loadOptions) {
                return (React.createElement("div", { className: "text-sm text-muted-foreground py-4 text-center" }, "No loadOptions configured"));
            }
            return (React.createElement(AutocompleteMultiEditor, { loadOptions: config.loadOptions, value: (value || []), onChange: onChange, placeholder: config.placeholder }));
        default:
            return null;
    }
}
// ---------------------------------------------------------------------------
// Multi-select editor (Tasks demo style: badges + search + checkboxes)
// ---------------------------------------------------------------------------
function MultiSelectEditor({ options, value, onChange, }) {
    const [search, setSearch] = useState('');
    const filtered = useMemo(() => {
        if (!search.trim())
            return options;
        const term = search.toLowerCase();
        return options.filter((opt) => opt.label.toLowerCase().includes(term));
    }, [options, search]);
    const toggle = (optValue) => {
        const next = value.includes(optValue)
            ? value.filter((v) => v !== optValue)
            : [...value, optValue];
        onChange(next);
    };
    const remove = (optValue) => {
        onChange(value.filter((v) => v !== optValue));
    };
    return (React.createElement("div", { className: "space-y-3" },
        value.length > 0 && (React.createElement("div", { className: "flex flex-wrap gap-1 pb-2 border-b" }, value.map((val) => {
            const opt = options.find((o) => o.value === val);
            return (React.createElement(BadgeAlt, { key: val, variant: "secondary", className: "flex items-center gap-1 pl-2 pr-1 py-0.5 text-xs" },
                (opt === null || opt === void 0 ? void 0 : opt.label) || val,
                React.createElement("button", { type: "button", onClick: () => remove(val), className: "rounded-full p-0.5 hover:bg-destructive/10", "aria-label": `Remove ${(opt === null || opt === void 0 ? void 0 : opt.label) || val}` },
                    React.createElement(X, { className: "size-3" }))));
        }))),
        React.createElement("div", { className: "flex gap-2" },
            React.createElement("div", { className: "relative flex-1" },
                React.createElement(Search, { className: "absolute left-2 top-2 size-3.5 text-muted-foreground" }),
                React.createElement(Input, { placeholder: "Search...", value: search, onChange: (e) => setSearch(e.target.value), className: "h-8 pl-7 text-sm" })),
            React.createElement(Button, { variant: "outline", size: "sm", className: "h-8", onClick: () => onChange(options.map((o) => o.value)) }, "All"),
            React.createElement(Button, { variant: "outline", size: "sm", className: "h-8", onClick: () => onChange([]) }, "Clear")),
        React.createElement("div", { className: "max-h-48 overflow-y-auto space-y-0.5" },
            filtered.map((opt) => {
                const checked = value.includes(opt.value);
                return (React.createElement("label", { key: opt.value, className: "flex items-center gap-2.5 px-2 py-1.5 hover:bg-accent rounded-md cursor-pointer text-sm" },
                    React.createElement(Checkbox, { checked: checked, onCheckedChange: () => toggle(opt.value) }),
                    React.createElement("span", { className: "flex-1" }, opt.label),
                    checked && React.createElement(Check, { className: "size-3.5 text-primary shrink-0" })));
            }),
            filtered.length === 0 && (React.createElement("p", { className: "text-sm text-muted-foreground text-center py-3" }, "No options found")))));
}
// ---------------------------------------------------------------------------
// Single-select editor
// ---------------------------------------------------------------------------
function SingleSelectEditor({ options, value, onChange, }) {
    return (React.createElement("div", { className: "space-y-0.5 max-h-48 overflow-y-auto" }, options.map((opt) => {
        const selected = opt.value === value;
        return (React.createElement("button", { key: opt.value, type: "button", onClick: () => onChange(selected ? null : opt.value), className: `flex items-center gap-2.5 w-full px-2 py-1.5 rounded-md cursor-pointer text-sm text-left ${selected ? 'bg-accent' : 'hover:bg-accent'}` },
            React.createElement("span", { className: "flex-1" }, opt.label),
            selected && React.createElement(Check, { className: "size-3.5 text-primary shrink-0" })));
    })));
}
// ---------------------------------------------------------------------------
// Text editor
// ---------------------------------------------------------------------------
function TextEditor({ value, onChange, placeholder, }) {
    return (React.createElement("div", { className: "flex gap-2" },
        React.createElement(Input, { value: value, onChange: (e) => onChange(e.target.value || null), placeholder: placeholder || 'Enter value...', className: "h-8 text-sm" }),
        value && (React.createElement(Button, { variant: "outline", size: "sm", className: "h-8", onClick: () => onChange(null) }, "Clear"))));
}
// ---------------------------------------------------------------------------
// Toggle editor
// ---------------------------------------------------------------------------
function ToggleEditor({ value, onChange, label, }) {
    return (React.createElement("label", { className: "flex items-center justify-between gap-3 cursor-pointer" },
        React.createElement("span", { className: "text-sm" }, label),
        React.createElement(Switch, { checked: value, onCheckedChange: (checked) => onChange(checked) })));
}
// ---------------------------------------------------------------------------
// Autocomplete single-select editor (inline async search)
// ---------------------------------------------------------------------------
function AutocompleteSingleEditor({ loadOptions, value, onChange, placeholder, }) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedLabel, setSelectedLabel] = useState(null);
    const reqId = useRef(0);
    const loadOptionsRef = useRef(loadOptions);
    loadOptionsRef.current = loadOptions;
    const debouncedSearch = useRef(debounce(async (q) => {
        const id = ++reqId.current;
        setLoading(true);
        try {
            const items = await loadOptionsRef.current(q);
            if (id === reqId.current)
                setResults(items);
        }
        catch (_a) {
            if (id === reqId.current)
                setResults([]);
        }
        finally {
            if (id === reqId.current)
                setLoading(false);
        }
    }, 300)).current;
    useEffect(() => () => debouncedSearch.cancel(), []);
    // Resolve label for current value
    useEffect(() => {
        if (!value) {
            setSelectedLabel(null);
            return;
        }
        let active = true;
        (async () => {
            try {
                const items = await loadOptionsRef.current(value);
                if (active) {
                    const match = items.find((i) => i.value === value);
                    setSelectedLabel(match ? match.label : null);
                }
            }
            catch (_a) {
                if (active)
                    setSelectedLabel(null);
            }
        })();
        return () => {
            active = false;
        };
    }, [value]);
    const handleQueryChange = useCallback((e) => {
        const q = e.target.value;
        setQuery(q);
        if (q.length >= 2) {
            debouncedSearch(q);
        }
        else {
            debouncedSearch.cancel();
            ++reqId.current;
            setLoading(false);
            setResults([]);
        }
    }, [debouncedSearch]);
    return (React.createElement("div", { className: "space-y-3" },
        value && (React.createElement("div", { className: "flex flex-wrap gap-1 pb-2 border-b" },
            React.createElement(BadgeAlt, { variant: "secondary", className: "flex items-center gap-1 pl-2 pr-1 py-0.5 text-xs" },
                selectedLabel || value,
                React.createElement("button", { type: "button", onClick: () => onChange(null), className: "rounded-full p-0.5 hover:bg-destructive/10", "aria-label": `Remove ${selectedLabel || value}` },
                    React.createElement(X, { className: "size-3" }))))),
        React.createElement("div", { className: "relative" },
            React.createElement(Search, { className: "absolute left-2 top-2 size-3.5 text-muted-foreground" }),
            React.createElement(Input, { placeholder: placeholder || 'Search...', value: query, onChange: handleQueryChange, className: "h-8 pl-7 text-sm", autoFocus: true }),
            loading && (React.createElement(Loader2, { className: "absolute right-2 top-2 size-3.5 animate-spin text-muted-foreground" }))),
        React.createElement("div", { className: "max-h-48 overflow-y-auto space-y-0.5" },
            results.map((opt) => {
                const selected = opt.value === value;
                return (React.createElement("button", { key: opt.value, type: "button", onClick: () => {
                        debouncedSearch.cancel();
                        ++reqId.current;
                        onChange(opt.value);
                        setSelectedLabel(opt.label);
                        setQuery('');
                        setResults([]);
                        setLoading(false);
                    }, className: `flex items-center gap-2.5 w-full px-2 py-1.5 rounded-md text-sm text-left ${selected
                        ? 'bg-accent cursor-default'
                        : 'hover:bg-accent cursor-pointer'}` },
                    React.createElement("span", { className: "flex-1" }, opt.label),
                    selected && React.createElement(Check, { className: "size-3.5 text-primary shrink-0" })));
            }),
            query.length >= 2 && !loading && results.length === 0 && (React.createElement("p", { className: "text-sm text-muted-foreground text-center py-3" }, "No results found")),
            query.length < 2 && !value && (React.createElement("p", { className: "text-xs text-muted-foreground text-center py-3" }, "Type at least 2 characters to search")))));
}
// ---------------------------------------------------------------------------
// Autocomplete multi-select editor (inline async search)
// ---------------------------------------------------------------------------
function AutocompleteMultiEditor({ loadOptions, value, onChange, placeholder, }) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [labels, setLabels] = useState({});
    const reqId = useRef(0);
    const inputRef = useRef(null);
    const loadOptionsRef = useRef(loadOptions);
    loadOptionsRef.current = loadOptions;
    const debouncedSearch = useRef(debounce(async (q) => {
        const id = ++reqId.current;
        setLoading(true);
        try {
            const items = await loadOptionsRef.current(q);
            if (id === reqId.current)
                setResults(items);
        }
        catch (_a) {
            if (id === reqId.current)
                setResults([]);
        }
        finally {
            if (id === reqId.current)
                setLoading(false);
        }
    }, 300)).current;
    useEffect(() => () => debouncedSearch.cancel(), []);
    // Hydrate labels for pre-selected values
    useEffect(() => {
        if (value.length === 0)
            return;
        const unlabeled = value.filter((id) => !labels[id]);
        if (unlabeled.length === 0)
            return;
        let active = true;
        const promises = unlabeled.map((id) => loadOptionsRef.current(id));
        Promise.allSettled(promises).then((results) => {
            if (!active)
                return;
            const newLabels = {};
            results.forEach((result, i) => {
                if (result.status === 'fulfilled') {
                    const match = result.value.find((item) => item.value === unlabeled[i]);
                    if (match)
                        newLabels[unlabeled[i]] = match.label;
                }
            });
            if (Object.keys(newLabels).length > 0) {
                setLabels((prev) => (Object.assign(Object.assign({}, prev), newLabels)));
            }
        });
        return () => {
            active = false;
        };
    }, [value, labels]);
    const handleQueryChange = useCallback((e) => {
        const q = e.target.value;
        setQuery(q);
        if (q.length >= 2) {
            debouncedSearch(q);
        }
        else {
            debouncedSearch.cancel();
            ++reqId.current;
            setLoading(false);
            setResults([]);
        }
    }, [debouncedSearch]);
    const handleSelect = useCallback((opt) => {
        var _a;
        if (!value.includes(opt.value)) {
            onChange([...value, opt.value]);
            setLabels((prev) => (Object.assign(Object.assign({}, prev), { [opt.value]: opt.label })));
        }
        setQuery('');
        setResults([]);
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, [value, onChange]);
    const handleRemove = useCallback((id) => {
        onChange(value.filter((v) => v !== id));
    }, [value, onChange]);
    const selectedSet = useMemo(() => new Set(value), [value]);
    return (React.createElement("div", { className: "space-y-3" },
        value.length > 0 && (React.createElement("div", { className: "flex flex-wrap gap-1 pb-2 border-b" }, value.map((id) => (React.createElement(BadgeAlt, { key: id, variant: "secondary", className: "flex items-center gap-1 pl-2 pr-1 py-0.5 text-xs" },
            labels[id] || id,
            React.createElement("button", { type: "button", onClick: () => handleRemove(id), className: "rounded-full p-0.5 hover:bg-destructive/10", "aria-label": `Remove ${labels[id] || id}` },
                React.createElement(X, { className: "size-3" }))))))),
        React.createElement("div", { className: "relative" },
            React.createElement(Search, { className: "absolute left-2 top-2 size-3.5 text-muted-foreground" }),
            React.createElement(Input, { ref: inputRef, placeholder: placeholder || 'Search...', value: query, onChange: handleQueryChange, className: "h-8 pl-7 text-sm", autoFocus: true }),
            loading && (React.createElement(Loader2, { className: "absolute right-2 top-2 size-3.5 animate-spin text-muted-foreground" }))),
        React.createElement("div", { className: "max-h-48 overflow-y-auto space-y-0.5" },
            results.map((opt) => {
                const isSelected = selectedSet.has(opt.value);
                return (React.createElement("button", { key: opt.value, type: "button", onClick: () => handleSelect(opt), disabled: isSelected, className: `flex items-center gap-2.5 w-full px-2 py-1.5 rounded-md text-sm text-left ${isSelected
                        ? 'bg-accent cursor-default'
                        : 'hover:bg-accent cursor-pointer'}` },
                    React.createElement("span", { className: "flex-1" }, opt.label),
                    isSelected && (React.createElement(Check, { className: "size-3.5 text-primary shrink-0" }))));
            }),
            query.length >= 2 && !loading && results.length === 0 && (React.createElement("p", { className: "text-sm text-muted-foreground text-center py-3" }, "No results found")),
            query.length < 2 && (React.createElement("p", { className: "text-xs text-muted-foreground text-center py-3" }, "Type at least 2 characters to search"))),
        value.length > 0 && (React.createElement(React.Fragment, null,
            React.createElement("div", { className: "border-t" }),
            React.createElement("button", { type: "button", className: "w-full text-center text-sm text-muted-foreground hover:text-foreground py-1 cursor-pointer", onClick: () => onChange([]) }, "Clear all")))));
}
